// Figma: login-default-empty / login-email-focused / login-loading-state /
// login-email-error / login-account-not-found (nodes 18050:151, 193, 236, 506, 287) — one
// unified screen (unlike LC2's old Login/SsoLogin/Register toggle). A single email field
// feeds two actions: Login (magic link) and Sign up.
//
// Google SSO removed per explicit request — LC2's SsoLogin mutation/button dropped from
// this card. The /verify_sso_login route + TokenVerification.js's SSO-callback branch are
// left as-is (out of scope, and still a valid landing page for anyone with an existing SSO
// link), just nothing in this card triggers that flow anymore.
//
// Enter-to-submit: the email field + Login button now live inside a real <form
// onSubmit={handleSubmit(runLogin)}>, so pressing Enter in the field submits Login (native
// browser form behavior) — previously the "form" was just a Stack with onClick handlers, so
// Enter did nothing. Sign up stays a plain type="button" so Enter never accidentally
// triggers it instead.
//
// LOGIC PORTED from LC2's LoginForm/Register: same yup email validation, same two mutations
// (useUpdateLoginSessionMutation / useUpdateSignUpSessionMutation), same
// removeCookie('session_token') before login.
import React from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import {yupResolver} from '@hookform/resolvers/yup';
import {useForm, Controller} from 'react-hook-form';
import {Stack, TextField, Typography} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import validateEmail from '../../commons/utils/emailValidation';
import {removeCookie} from '../../commons/utils/cookies';
import {useUpdateLoginSessionMutation, useUpdateSignUpSessionMutation} from '../../features/loyaltyCloudApi/session';

import AuthCard from './components/AuthCard';
import AuthLogo from './components/AuthLogo';
import AlertBanner from './components/AlertBanner';
import SpinnerIcon from './components/SpinnerIcon';
import buttonSpinnerIcon from '../../assets/auth/button-spinner-icon.svg';
import {classifyLoginError} from './authErrorHelpers';

const emailSchema = Yup.object({
    email: Yup.string()
        .required('Email is required')
        .test('is-valid-email', 'Please enter a valid email address', (value) => validateEmail(value?.replace(/\s/g, '')))
});

const LoginCard = ({alertBanner, onClearAlert, onLoginSuccess, onSignUpSuccess, onError}) => {
    const [updateLoginSession, {isLoading: isLoggingIn}] = useUpdateLoginSessionMutation();
    const [updateSignUpSession, {isLoading: isSigningUp}] = useUpdateSignUpSessionMutation();

    const {control, handleSubmit} = useForm({
        mode: 'onBlur',
        defaultValues: {email: ''},
        resolver: yupResolver(emailSchema)
    });

    const runLogin = async ({email}) => {
        onClearAlert();
        removeCookie('session_token');
        const res = await updateLoginSession(email.toLowerCase());
        if ('error' in res) {
            onError(classifyLoginError(res.error));
        } else {
            onLoginSuccess(email.toLowerCase());
        }
    };

    const runSignUp = async ({email}) => {
        onClearAlert();
        const res = await updateSignUpSession(email.toLowerCase());
        if ('error' in res) {
            onError(classifyLoginError(res.error));
        } else {
            onSignUpSuccess(email.toLowerCase());
        }
    };

    // Editing the email again after a persistent alert should clear it, same spirit as
    // LC2's `handleReset` dispatching resetSession() on user-initiated retry.
    const handleEmailChange = (fieldOnChange) => (event) => {
        if (alertBanner) onClearAlert();
        fieldOnChange(event);
    };

    return (
        <AuthCard>
            <Stack alignItems="center" spacing={3} sx={{width: '100%'}}>
                <AuthLogo />
                {alertBanner && <AlertBanner title={alertBanner.title} description={alertBanner.description} />}
                <Typography variant="h4" textAlign="center">
                    Login to Stake Loyalty Cloud
                </Typography>
            </Stack>

            <Stack component="form" onSubmit={handleSubmit(runLogin)} spacing={3} sx={{width: '100%'}}>
                <Controller
                    name="email"
                    control={control}
                    render={({field, fieldState}) => (
                        <Stack spacing={1} sx={{width: '100%'}}>
                            <Typography variant="body2" fontWeight={500}>
                                Email address
                            </Typography>
                            <TextField
                                {...field}
                                onChange={handleEmailChange(field.onChange)}
                                fullWidth
                                placeholder="your@email.com"
                                error={!!fieldState.error}
                            />
                            {fieldState.error && (
                                <Typography variant="caption" color="error.main" fontWeight={500}>
                                    {fieldState.error.message}
                                </Typography>
                            )}
                        </Stack>
                    )}
                />

                <Stack spacing={1} sx={{width: '100%'}}>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        fullWidth
                        loading={isLoggingIn}
                        loadingIndicator={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <SpinnerIcon src={buttonSpinnerIcon} />
                                <span>Logging in...</span>
                            </Stack>
                        }
                    >
                        Login
                    </LoadingButton>
                    <LoadingButton
                        type="button"
                        onClick={handleSubmit(runSignUp)}
                        variant="outlined"
                        fullWidth
                        loading={isSigningUp}
                    >
                        Sign up
                    </LoadingButton>
                </Stack>
            </Stack>
        </AuthCard>
    );
};

LoginCard.propTypes = {
    alertBanner: PropTypes.shape({title: PropTypes.string, description: PropTypes.node}),
    onClearAlert: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func.isRequired,
    onSignUpSuccess: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired
};

export default LoginCard;
